import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Phone, MapPin, MessageCircle, Loader2 } from 'lucide-react';
import { CONTACT, SECTIONS } from '@/constants/testIds';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contacto() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [status, setStatus] = useState({ loading: false, success: false, error: '' });

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, success: false, error: '' });
    try {
      await axios.post(`${API}/contact`, form);
      setStatus({ loading: false, success: true, error: '' });
      setForm({ name: '', email: '', phone: '', message: '' });
      setTimeout(() => setStatus((s) => ({ ...s, success: false })), 6000);
    } catch (err) {
      setStatus({ loading: false, success: false, error: 'Ocurrió un error. Intente nuevamente.' });
    }
  };

  const whatsappLink = 'https://wa.me/50670894810?text=' + encodeURIComponent('Hola, me gustaría más información sobre Agropecuaria Tres Cerritos.');

  return (
    <section id="contacto" data-testid={SECTIONS.contacto} className="bg-[#F5EFE0] py-24">
      <div className="max-w-[1300px] mx-auto px-6 lg:px-10">
        <div className="text-center mb-14">
          <span className="gold-divider font-cinzel text-[#A88340] text-xs tracking-[0.3em]">CONTÁCTENOS</span>
          <h2 className="font-cormorant text-[#0F3527] text-4xl lg:text-5xl mt-4">
            Conversemos sobre el futuro de la ganadería
          </h2>
        </div>

        <div className="grid lg:grid-cols-5 gap-10">
          {/* Info column */}
          <div className="lg:col-span-2 bg-[#0F3527] text-[#F5EFE0] p-10 rounded-sm tc-grain">
            <p className="font-cinzel text-[#C9A361] text-xs tracking-[0.3em] mb-6">INFORMACIÓN</p>

            <div className="space-y-6">
              <InfoRow icon={MapPin} label="Ubicación">
                Higuerillas de Abangares,<br/>Guanacaste, Costa Rica
              </InfoRow>
              <InfoRow icon={Mail} label="Correo">
                <a href="mailto:info@trescerritos.com" className="hover:text-[#C9A361] transition-colors">
                  info@trescerritos.com
                </a>
              </InfoRow>
              <InfoRow icon={Phone} label="Teléfono">
                <a href="tel:+50670894810" className="hover:text-[#C9A361] transition-colors">
                  +506 7089 4810
                </a>
              </InfoRow>
            </div>

            <a
              href={whatsappLink}
              target="_blank"
              rel="noreferrer"
              data-testid={CONTACT.whatsapp}
              className="btn-whats mt-10 inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium"
            >
              <MessageCircle size={18} />
              WhatsApp
            </a>
          </div>

          {/* Form */}
          <form
            onSubmit={handleSubmit}
            data-testid={CONTACT.form}
            className="lg:col-span-3 bg-white/70 p-10 rounded-sm border border-[#0F3527]/8 shadow-sm space-y-5"
          >
            <Field label="Nombre completo *" name="name">
              <input
                data-testid={CONTACT.name}
                required
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                className="w-full bg-transparent border-b border-[#0F3527]/30 focus:border-[#0F3527] outline-none py-2 text-[#0F3527]"
              />
            </Field>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Correo electrónico *" name="email">
                <input
                  data-testid={CONTACT.email}
                  required
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  className="w-full bg-transparent border-b border-[#0F3527]/30 focus:border-[#0F3527] outline-none py-2 text-[#0F3527]"
                />
              </Field>
              <Field label="Teléfono" name="phone">
                <input
                  data-testid={CONTACT.phone}
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  className="w-full bg-transparent border-b border-[#0F3527]/30 focus:border-[#0F3527] outline-none py-2 text-[#0F3527]"
                />
              </Field>
            </div>
            <Field label="Mensaje *" name="message">
              <textarea
                data-testid={CONTACT.message}
                required
                name="message"
                rows={4}
                value={form.message}
                onChange={handleChange}
                className="w-full bg-transparent border-b border-[#0F3527]/30 focus:border-[#0F3527] outline-none py-2 text-[#0F3527] resize-none"
              />
            </Field>

            {status.error && (
              <p data-testid={CONTACT.errorAlert} className="text-red-700 bg-red-50 px-4 py-3 rounded-sm text-sm">
                {status.error}
              </p>
            )}
            {status.success && (
              <p data-testid={CONTACT.successAlert} className="text-[#0F3527] bg-[#C9A361]/20 border border-[#C9A361]/40 px-4 py-3 rounded-sm text-sm">
                ¡Gracias! Su mensaje ha sido enviado correctamente. Le contactaremos pronto.
              </p>
            )}

            <button
              type="submit"
              data-testid={CONTACT.submit}
              disabled={status.loading}
              className="btn-primary rounded-full px-8 py-3.5 text-sm tracking-[0.15em] font-cinzel uppercase inline-flex items-center gap-2 disabled:opacity-70"
            >
              {status.loading && <Loader2 size={16} className="animate-spin" />}
              Enviar Mensaje
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-cinzel text-[11px] tracking-[0.18em] uppercase text-[#0F3527]/70">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function InfoRow({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-4">
      <span className="text-[#C9A361] mt-1"><Icon size={20} /></span>
      <div>
        <p className="font-cinzel text-[11px] tracking-[0.2em] uppercase text-[#C9A361]/80 mb-1">{label}</p>
        <p className="text-[#F5EFE0]/90">{children}</p>
      </div>
    </div>
  );
}
